<script setup>
import { computed } from 'vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ProjectDot from '@/components/project/ProjectDot.vue'

/**
 * The one confirmation for every way of taking someone off the team, phrased
 * for what the server will actually do:
 *
 *  - remove from THIS workspace: revokes one grant; but when it is the person's
 *    only workspace, the server also offboards them and frees the seat, and the
 *    dialog says so before the click rather than after.
 *  - remove from the ACCOUNT: every workspace at once, seat freed.
 *
 * Pending invitations follow the same one-vs-all shape.
 */
const props = defineProps({
	modelValue: { type: Boolean, default: false },
	member: { type: Object, default: null },
	// 'project' | 'account'
	scope: { type: String, default: 'account' },
	activeProject: { type: Object, default: null },
	// Whether the account has more than one workspace. With one, naming
	// workspaces in the copy would be jargon: it is just "the team".
	multiProject: { type: Boolean, default: false },
	busy: { type: Boolean, default: false },
	error: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const name = computed(() => props.member?.name || props.member?.email || 'This person')
const isPending = computed(() => props.member?.status === 'pending')
const projectName = computed(() => props.activeProject?.name || 'this workspace')
// A single-project account has nothing to scope to: every removal is "from
// the team", whatever the row's grant list says.
const scoped = computed(() => props.multiProject && !!props.activeProject?.id)
const workspaces = computed(() => (
	scoped.value && Array.isArray(props.member?.workspaces) ? props.member.workspaces : []
))
const others = computed(() => workspaces.value.filter((w) => w.id !== props.activeProject.id))
const isProjectScope = computed(() => scoped.value && props.scope === 'project')
const onlyProject = computed(() => isProjectScope.value && others.value.length === 0)

const listNames = (items) => {
	const names = items.map((w) => w.name)
	if (names.length <= 1) return names.join('')
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

const title = computed(() => {
	if (isPending.value) {
		if (isProjectScope.value && others.value.length) return `Cancel the invitation to ${projectName.value}?`
		if (workspaces.value.length > 1) return `Cancel all of ${name.value}'s invitations?`
		return `Cancel ${name.value}'s invitation?`
	}
	if (isProjectScope.value) return `Remove ${name.value} from ${projectName.value}?`
	return scoped.value ? `Remove ${name.value} from the account?` : `Remove ${name.value} from the team?`
})

const body = computed(() => {
	if (isPending.value) {
		if (isProjectScope.value && others.value.length) {
			return `${name.value}'s invitations to ${listNames(others.value)} stay pending, so their seat stays reserved.`
		}
		if (workspaces.value.length > 1) {
			return `The invitations to ${listNames(workspaces.value)} are withdrawn and the seat they were holding is freed.`
		}
		return scoped.value
			? `The invitation to ${projectName.value} is withdrawn and the seat it was holding is freed.`
			: 'The invitation is withdrawn and the seat it was holding is freed.'
	}
	if (isProjectScope.value) {
		if (onlyProject.value) {
			return `This is ${name.value}'s only workspace. Removing them here also removes them from the account and frees their seat.`
		}
		return `${name.value} loses access to ${projectName.value} but keeps ${listNames(others.value)}. Their seat stays in use.`
	}
	if (workspaces.value.length > 1) {
		return `${name.value} is removed from ${listNames(workspaces.value)} and their seat is freed. You can invite them again later.`
	}
	return `${name.value} is removed from the team and their seat is freed. You can invite them again later.`
})

const confirmLabel = computed(() => {
	if (isPending.value) {
		if (isProjectScope.value && others.value.length) return 'Cancel this invitation'
		return workspaces.value.length > 1 ? 'Cancel all invitations' : 'Cancel invitation'
	}
	if (isProjectScope.value) return onlyProject.value ? 'Remove and free seat' : `Remove from ${projectName.value}`
	return scoped.value ? 'Remove from account' : 'Remove member'
})

// What the person keeps, shown as chips so the owner can check the list
// against what they meant to do.
const keeps = computed(() => (isProjectScope.value && !isPending.value ? others.value : []))

const close = () => {
	if (props.busy) return
	isOpen.value = false
}
const confirm = () => {
	if (props.busy) return
	emit('confirm', { member: props.member, scope: props.scope })
}
</script>

<template>
	<ModalBox v-model="isOpen" :title="title" size="sm" mobile-placement="bottom" :close-on-backdrop="!busy">
		<div class="px-4 sm:px-6 pb-2" data-testid="team-remove-dialog">
			<p class="text-sm text-surface-8 mb-0">{{ body }}</p>
			<div v-if="keeps.length" class="mt-3 flex flex-wrap items-center gap-1.5">
				<span class="text-tiny text-surface-6">Keeps:</span>
				<span
					v-for="workspace in keeps"
					:key="workspace.id || workspace.name"
					class="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 text-tiny text-surface-text-1"
				>
					<ProjectDot :color="workspace.color" size="size-1.5" />
					{{ workspace.name }}
				</span>
			</div>
			<Alert v-if="error" type="error" class="mt-4" :text="error" />
		</div>
		<template #footer>
			<div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 px-4 sm:px-6 py-4 border-t border-surface-3">
				<FormButton type="secondary" size="md" class="w-full sm:w-auto justify-center" :disabled="busy" @click="close">Keep</FormButton>
				<FormButton
					type="danger"
					size="md"
					class="w-full sm:w-auto justify-center"
					:disabled="busy"
					:is-loading="busy"
					@click="confirm"
				>
					{{ confirmLabel }}
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
